// Database access hub: wires together all smaller data modules and re-exports shared helpers.
// Each module keeps responsibility for a slice of the schema so this file stays approachable.

import type { ClientsModule } from './db/modules/clients';
import { registerClients } from './db/modules/clients';
import type { UsersModule } from './db/modules/generated/users';
import { registerUsers } from './db/modules/generated/users';
import type { MatterTypesModule } from './db/modules/generated/matterTypes';
import { registerMatterTypes } from './db/modules/generated/matterTypes';
import type { MatterTypeFieldsModule } from './db/modules/generated/matterTypeFields';
import { registerMatterTypeFields } from './db/modules/generated/matterTypeFields';
import type { MatterFieldValuesModule } from './db/modules/generated/matterFieldValues';
import { registerMatterFieldValues } from './db/modules/generated/matterFieldValues';
import type { GoalsModule } from './db/modules/generated/goals';
import { registerGoals } from './db/modules/generated/goals';
import type { GoalTemplatesModule } from './db/modules/generated/goalTemplates';
import { registerGoalTemplates } from './db/modules/generated/goalTemplates';
import type { TaskTemplatesModule } from './db/modules/generated/taskTemplates';
import { registerTaskTemplates } from './db/modules/generated/taskTemplates';
import type { UserHiddenTemplatesModule } from './db/modules/generated/userHiddenTemplates';
import { registerUserHiddenTemplates } from './db/modules/generated/userHiddenTemplates';
import type { MattersModule } from './db/modules/generated/matters';
import { registerMatters } from './db/modules/generated/matters';
import type { ProjectsModule } from './db/modules/generated/projects';
import { registerProjects } from './db/modules/generated/projects';
import type { RegularTaskHistoryModule } from './db/modules/generated/regularTaskHistory';
import { registerRegularTaskHistory } from './db/modules/generated/regularTaskHistory';
import type { TaskCompletionStatisticsModule } from './db/modules/generated/taskCompletionStatistics';
import { registerTaskCompletionStatistics } from './db/modules/generated/taskCompletionStatistics';
import type { MatterTemplatesModule } from './db/modules/generated/matterTemplates';
import { registerMatterTemplates } from './db/modules/generated/matterTemplates';
import type { MatterTemplateDeliverablesModule } from './db/modules/generated/matterTemplateDeliverables';
import { registerMatterTemplateDeliverables } from './db/modules/generated/matterTemplateDeliverables';
import type { ProjectTemplatesModule } from './db/modules/generated/projectTemplates';
import { registerProjectTemplates } from './db/modules/generated/projectTemplates';
import type { ProjectTemplateMattersModule } from './db/modules/generated/projectTemplateMatters';
import { registerProjectTemplateMatters } from './db/modules/generated/projectTemplateMatters';
import type { ProjectTemplateDeliverablesModule } from './db/modules/generated/projectTemplateDeliverables';
import { registerProjectTemplateDeliverables } from './db/modules/generated/projectTemplateDeliverables';
import type { DocumentsModule } from './db/modules/generated/documents';
import { registerDocuments } from './db/modules/generated/documents';
import type { DocumentFoldersModule } from './db/modules/generated/documentFolders';
import { registerDocumentFolders } from './db/modules/generated/documentFolders';
import type { DocumentAssociationsModule } from './db/modules/generated/documentAssociations';
import { registerDocumentAssociations } from './db/modules/generated/documentAssociations';
import type { DocumentReviewSessionsModule } from './db/modules/generated/documentReviewSessions';
import { registerDocumentReviewSessions } from './db/modules/generated/documentReviewSessions';
import type { MatterDeadlinesModule } from './db/modules/generated/matterDeadlines';
import { registerMatterDeadlines } from './db/modules/generated/matterDeadlines';
import type { DocumentActionsModule } from './db/modules/generated/documentActions';
import { registerDocumentActions } from './db/modules/generated/documentActions';
import type { DeadlinesModule } from './db/modules/generated/deadlines';
import { registerDeadlines } from './db/modules/generated/deadlines';
import type { BreakRecordsModule } from './db/modules/generated/breakRecords';
import { registerBreakRecords } from './db/modules/generated/breakRecords';
import type { TimeEntriesModule } from './db/modules/timeEntries';
import { registerTimeEntries } from './db/modules/timeEntries';
import type { TasksModule } from './db/modules/tasks';
import { registerTasks } from './db/modules/tasks';

// Ensure the type reflects every registered module so downstream code has strong IntelliSense.
type DatabaseModules = {
  clients: ClientsModule;
  users: UsersModule;
  matterTypes: MatterTypesModule;
  matterTypeFields: MatterTypeFieldsModule;
  matterFieldValues: MatterFieldValuesModule;
  goals: GoalsModule;
  goalTemplates: GoalTemplatesModule;
  taskTemplates: TaskTemplatesModule;
  userHiddenTemplates: UserHiddenTemplatesModule;
  matters: MattersModule;
  projects: ProjectsModule;
  regularTaskHistory: RegularTaskHistoryModule;
  taskCompletionStatistics: TaskCompletionStatisticsModule;
  matterTemplates: MatterTemplatesModule;
  matterTemplateDeliverables: MatterTemplateDeliverablesModule;
  projectTemplates: ProjectTemplatesModule;
  projectTemplateMatters: ProjectTemplateMattersModule;
  projectTemplateDeliverables: ProjectTemplateDeliverablesModule;
  documents: DocumentsModule;
  documentFolders: DocumentFoldersModule;
  documentAssociations: DocumentAssociationsModule;
  documentReviewSessions: DocumentReviewSessionsModule;
  matterDeadlines: MatterDeadlinesModule;
  documentActions: DocumentActionsModule;
  deadlines: DeadlinesModule;
  breakRecords: BreakRecordsModule;
  timeEntries: TimeEntriesModule;
  tasks: TasksModule;
  // Convenience helpers surfaced on db
  createTasksFromTemplateWithDependencies?: (goalId: string, goalTemplateId: string, userId: string) => Promise<void>;
};

const modules: Partial<DatabaseModules> = {};
registerClients(modules);
registerUsers(modules);
registerMatterTypes(modules);
registerMatterTypeFields(modules);
registerMatterFieldValues(modules);
registerGoals(modules);
registerGoalTemplates(modules);
registerTaskTemplates(modules);
registerUserHiddenTemplates(modules);
registerMatters(modules);
registerProjects(modules);
registerRegularTaskHistory(modules);
registerTaskCompletionStatistics(modules);
registerMatterTemplates(modules);
registerMatterTemplateDeliverables(modules);
registerProjectTemplates(modules);
registerProjectTemplateMatters(modules);
registerProjectTemplateDeliverables(modules);
registerDocuments(modules);
registerDocumentFolders(modules);
registerDocumentAssociations(modules);
registerDocumentReviewSessions(modules);
registerMatterDeadlines(modules);
registerDocumentActions(modules);
registerDeadlines(modules);
registerBreakRecords(modules);
registerTimeEntries(modules);
registerTasks(modules);

// Attach convenience helper to the db object so callers can use db.createTasksFromTemplateWithDependencies(...)
(modules as any).createTasksFromTemplateWithDependencies = createTasksFromTemplateWithDependencies;

export const db = modules as DatabaseModules;

// Re-export helpers so callers keep the exact same import surface as before the refactor.
export { sql, checkDatabaseConnection } from './db/base';
export * from './db/types';

// Convenience helper: create tasks for a new deliverable from a goal template,
// wiring up dependencies based on template positions.
// This mirrors prior app behavior and keeps UI calls simple.
// Note: Offsets (offset_from_position, offset_days) are currently ignored here,
// because the public task mutations do not expose offset columns. Dependencies
// are respected via depends_on + dependency_type/group.
// Safe for multiple runs: it always creates a fresh set for the given goal_id.
export async function createTasksFromTemplateWithDependencies(
  goalId: string,
  goalTemplateId: string,
  userId: string
) {
  try {
    // 1) Fetch task templates for this goal template
    const templates = await (modules.taskTemplates as any).findByGoalTemplateId(goalTemplateId);
    if (!templates || templates.length === 0) {
      return;
    }

    // 2) First pass: create all tasks without dependencies, keep a map position -> id
    const createdByPosition = new Map<number, string>();
    const createdTasks: any[] = [];

    for (const t of templates) {
      const created = await (modules.tasks as any).create({
        goal_id: goalId,
        user_id: userId,
        title: t.title,
        description: t.description || null,
        priority: t.priority || 'Medium',
        status: 'To Do',
        task_type: 'To Do',
        position: t.position ?? 0,
        is_regular: false,
      });
      createdByPosition.set(Number(t.position ?? 0), created.id);
      createdTasks.push({ template: t, task: created });
    }

    // 3) Second pass: update dependencies now that we have all IDs
    for (const { template, task } of createdTasks) {
      const deps: string[] = [];
      const positions: number[] = Array.isArray(template.depends_on_positions) ? template.depends_on_positions : [];
      for (const p of positions) {
        const depId = createdByPosition.get(Number(p));
        if (depId) deps.push(depId);
      }
      if (deps.length > 0 || template.dependency_type || template.dependency_group) {
        await (modules.tasks as any).update(task.id, userId, {
          depends_on: deps,
          dependency_type: template.dependency_type || 'none',
          dependency_group: template.dependency_group || null,
          // Make blocked tasks non-live until deps are satisfied
          is_live: deps.length === 0 ? true : false,
        });
      }
    }
  } catch (err) {
    console.error('Error creating tasks from template:', err);
    throw err;
  }
}
